import { SkillVisibility, SkillWritePayload } from "@models";
import { Business, Groups, Lock, Public } from "@mui/icons-material";
import type { TextFieldProps } from "@mui/material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import GenericModal from "src/components/GenericModal/GenericModal";
import { AiGradientButton } from "src/components/ui/AiGradientButton";
import {
  getSkillVisibilityOptions,
  isSkillNameKebabCase,
  SKILL_NAME_KEBAB_CASE_ERROR,
  SKILL_NAME_KEBAB_CASE_HELPER_TEXT,
} from "./skill-utils";

export type SkillFormMode =
  | "create"
  | "edit"
  | "duplicate"
  | "upload"
  | "chat-draft";

export type SkillFormValues = Omit<
  SkillWritePayload,
  "enabled" | "forceEnabled"
> & {
  id?: string;
};

const visibilityIconByValue: Record<SkillVisibility, React.ReactNode> = {
  PRIVATE: <Lock fontSize="small" />,
  ORGANIZATION: <Business fontSize="small" />,
  GLOBAL: <Public fontSize="small" />,
  FORMLY_INTERNAL: <Groups fontSize="small" />,
};

const skillFormTitleByMode: Record<SkillFormMode, string> = {
  create: "Create skill",
  edit: "Edit skill",
  duplicate: "Duplicate skill",
  upload: "Upload skill",
  "chat-draft": "Create skill from chat",
};

export const skillFormDescriptionByMode: Record<SkillFormMode, string> = {
  create: "Define reusable instructions the agent can apply during a run.",
  edit: "Update the current skill directly.",
  duplicate: "Create an editable copy with the content prefilled.",
  upload: "Review the markdown file before saving it as a skill.",
  "chat-draft": "Review the generated draft before saving it as a skill.",
};

export const draftWithAgentHelperTextByMode: Partial<
  Record<SkillFormMode, string>
> = {
  create: "Not sure how to create a skill?",
  edit: "Want help improving this skill?",
  duplicate: "Want help adapting this copy?",
  upload: "Want help shaping this upload into a skill?",
};

export const getSkillFormValues = (
  initialValues?: Partial<SkillFormValues>
): SkillFormValues => ({
  id: initialValues?.id,
  name: initialValues?.name ?? "",
  descriptionMd: initialValues?.descriptionMd ?? "",
  contentMd: initialValues?.contentMd ?? "",
  visibility: initialValues?.visibility ?? "PRIVATE",
});

export const isSkillFormValid = (values: SkillFormValues) => {
  return (
    isSkillNameKebabCase(values.name) &&
    values.descriptionMd.trim().length > 0 &&
    values.contentMd.trim().length > 0
  );
};

export const isSkillFormNameInvalid = (name: string) =>
  name.trim().length > 0 && !isSkillNameKebabCase(name);

export const getSkillFormNameHelperText = (name: string) =>
  isSkillFormNameInvalid(name)
    ? SKILL_NAME_KEBAB_CASE_ERROR
    : SKILL_NAME_KEBAB_CASE_HELPER_TEXT;

export const areSkillFormValuesDirty = (
  values: SkillFormValues,
  initialValues?: Partial<SkillFormValues>
) => {
  const initial = getSkillFormValues(initialValues);

  return (
    values.name !== initial.name ||
    values.descriptionMd !== initial.descriptionMd ||
    values.contentMd !== initial.contentMd ||
    values.visibility !== initial.visibility
  );
};

export const toSkillWritePayload = (
  values: SkillFormValues
): SkillWritePayload => ({
  name: values.name,
  descriptionMd: values.descriptionMd,
  contentMd: values.contentMd,
  visibility: values.visibility,
});

export function SkillFormFields({
  values,
  onChange,
  canCreateGlobal,
  canCreateFormlyInternal,
  onDraftWithAgent,
  draftWithAgentLabel = "Draft with Agent",
  draftWithAgentHelperText,
  disabled = false,
  nameError = false,
  nameHelperText,
  namePlaceholder = "mdr-classification",
  descriptionPlaceholder = "What this skill does and when to use it.",
  descriptionSize,
  contentLabel = "Content",
  contentPlaceholder = "# Skill name\n\nWrite the instructions the agent should follow when this skill is applied.",
  contentMinRows = 10,
  includeUnavailableVisibilities = false,
  showSharedVisibilityWarning = true,
}: {
  values: SkillFormValues;
  onChange: (values: SkillFormValues) => void;
  canCreateGlobal: boolean;
  canCreateFormlyInternal: boolean;
  onDraftWithAgent?: (values: SkillFormValues) => void;
  draftWithAgentLabel?: string;
  draftWithAgentHelperText?: string;
  disabled?: boolean;
  nameError?: boolean;
  nameHelperText?: string;
  namePlaceholder?: string | null;
  descriptionPlaceholder?: string | null;
  descriptionSize?: TextFieldProps["size"];
  contentLabel?: string;
  contentPlaceholder?: string | null;
  contentMinRows?: number;
  includeUnavailableVisibilities?: boolean;
  showSharedVisibilityWarning?: boolean;
}) {
  return (
    <Stack
      gap={2}
      sx={{ pt: 1, minWidth: 0, maxWidth: "100%", boxSizing: "border-box" }}
    >
      {onDraftWithAgent ? (
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          flexWrap="wrap"
          sx={{ alignSelf: "flex-start", maxWidth: "100%", minWidth: 0 }}
        >
          {draftWithAgentHelperText ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ minWidth: 0, overflowWrap: "anywhere" }}
            >
              {draftWithAgentHelperText}
            </Typography>
          ) : null}
          <AiGradientButton
            size="small"
            onClick={() => onDraftWithAgent(values)}
            sx={{ flexShrink: 0, textTransform: "none" }}
          >
            {draftWithAgentLabel}
          </AiGradientButton>
        </Stack>
      ) : null}

      <TextField
        label="Name"
        value={values.name}
        onChange={(event) =>
          onChange({
            ...values,
            name: event.target.value,
          })
        }
        placeholder={namePlaceholder ?? undefined}
        size="small"
        fullWidth
        disabled={disabled}
        error={nameError}
        helperText={nameHelperText}
        FormHelperTextProps={{ sx: { mx: 0 } }}
      />

      <TextField
        label="Description"
        value={values.descriptionMd}
        onChange={(event) =>
          onChange({
            ...values,
            descriptionMd: event.target.value,
          })
        }
        placeholder={descriptionPlaceholder ?? undefined}
        size={descriptionSize}
        minRows={2}
        multiline
        fullWidth
        disabled={disabled}
      />

      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={700}
          textTransform="uppercase"
        >
          Visibility
        </Typography>
        <Box
          role="radiogroup"
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
            },
            gap: 1,
            mt: 0.75,
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            boxSizing: "border-box",
          }}
        >
          {getSkillVisibilityOptions({
            canCreateGlobal,
            canCreateFormlyInternal,
            includeUnavailable: includeUnavailableVisibilities,
          }).map((option) => {
            const selected = values.visibility === option.value;

            return (
              <Button
                key={option.value}
                role="radio"
                aria-checked={selected}
                disabled={disabled || option.disabled}
                onClick={() =>
                  onChange({
                    ...values,
                    visibility: option.value,
                  })
                }
                variant="outlined"
                color={selected ? "primary" : "inherit"}
                sx={{
                  justifyContent: "flex-start",
                  textAlign: "left",
                  p: 1.25,
                  borderRadius: 1,
                  textTransform: "none",
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  boxSizing: "border-box",
                  overflow: "hidden",
                  ...(selected && {
                    bgcolor: "#5d85ff2b",
                    color: "text.primary",
                    "&:hover": {
                      bgcolor: "#5d85ff2b",
                    },
                  }),
                }}
              >
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  gap={1}
                  sx={{ width: "100%", minWidth: 0 }}
                >
                  <Box sx={{ flexShrink: 0, display: "flex" }}>
                    {visibilityIconByValue[option.value]}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        overflowWrap: "anywhere",
                        whiteSpace: "normal",
                      }}
                    >
                      {option.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        overflowWrap: "anywhere",
                        whiteSpace: "normal",
                      }}
                    >
                      {option.disabled ? "Admin only" : option.description}
                    </Typography>
                  </Box>
                </Stack>
              </Button>
            );
          })}
        </Box>
      </Box>

      {showSharedVisibilityWarning &&
      (values.visibility === "GLOBAL" ||
        values.visibility === "FORMLY_INTERNAL") ? (
        <Alert
          severity="warning"
          sx={{
            "&&": {
              alignItems: "center",
              alignSelf: "stretch",
              display: "flex",
              flexDirection: "row",
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              boxSizing: "border-box",
            },
            "& .MuiAlert-icon": {
              alignSelf: "center",
              flexShrink: 0,
              py: 0,
            },
            "& .MuiAlert-message": {
              flex: "1 1 auto",
              minWidth: 0,
              width: "100%",
              py: 0,
              overflowWrap: "anywhere",
            },
          }}
        >
          This skill can be used outside your private workspace. Review the
          content carefully before saving.
        </Alert>
      ) : null}

      <TextField
        label={contentLabel}
        value={values.contentMd}
        onChange={(event) =>
          onChange({
            ...values,
            contentMd: event.target.value,
          })
        }
        placeholder={contentPlaceholder ?? undefined}
        minRows={contentMinRows}
        multiline
        fullWidth
        disabled={disabled}
        InputProps={{
          sx: {
            backgroundColor: "grey.50",
            fontFamily:
              "JetBrains Mono, ui-monospace, SFMono-Regular, monospace",
            fontSize: 13,
            lineHeight: 1.55,
            "&:hover": {
              backgroundColor: "grey.50",
            },
            "&.Mui-focused": {
              backgroundColor: "grey.50",
            },
          },
        }}
      />
    </Stack>
  );
}

type SkillFormModalState = {
  draftLoading?: boolean;
  isLoading?: boolean;
  isOpen: boolean;
};

type SkillFormModalPermissions = {
  canCreateFormlyInternal: boolean;
  canCreateGlobal: boolean;
};

export function SkillFormModal({
  mode,
  initialValues,
  permissions,
  state,
  onCancel,
  onDraftWithAgent,
  onSave,
}: {
  mode: SkillFormMode;
  initialValues?: Partial<SkillFormValues>;
  permissions: SkillFormModalPermissions;
  state: SkillFormModalState;
  onCancel: () => void;
  onDraftWithAgent?: (values: SkillFormValues) => void;
  onSave: (values: SkillFormValues) => void;
}) {
  const { draftLoading = false, isOpen } = state;
  const [values, setValues] = useState<SkillFormValues>({
    name: "",
    descriptionMd: "",
    contentMd: "",
    visibility: "PRIVATE",
  });

  useEffect(() => {
    if (!isOpen) return;
    setValues(getSkillFormValues(initialValues));
  }, [initialValues, isOpen]);

  const isValid = useMemo(() => isSkillFormValid(values), [values]);

  return (
    <GenericModal
      isOpen={isOpen}
      isDisabled={!isValid || draftLoading}
      isLoading={state.isLoading ?? false}
      handleCancel={onCancel}
      handleSave={() => onSave(values)}
      title={skillFormTitleByMode[mode]}
      description={skillFormDescriptionByMode[mode]}
      saveText="Save"
      width="620px"
    >
      {draftLoading ? (
        <Stack direction="row" alignItems="center" gap={1}>
          <CircularProgress size={24} />
          <Typography color="text.secondary">Creating skill…</Typography>
        </Stack>
      ) : (
        <SkillFormFields
          values={values}
          onChange={setValues}
          canCreateGlobal={permissions.canCreateGlobal}
          canCreateFormlyInternal={permissions.canCreateFormlyInternal}
          onDraftWithAgent={mode === "create" ? onDraftWithAgent : undefined}
          draftWithAgentHelperText={draftWithAgentHelperTextByMode[mode]}
          nameError={isSkillFormNameInvalid(values.name)}
          nameHelperText={getSkillFormNameHelperText(values.name)}
        />
      )}
    </GenericModal>
  );
}
